// src/layouts/products/components/ProductDetailsView/ProductRulesTab.js
import React, { useState } from 'react';

// --- Material-UI Core Components ---
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Divider,
  Card,
} from '@mui/material';
import { grey } from '@mui/material/colors';

// --- Argon Design System Components ---
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

/**
 * Renders a table for the rule's conditions.
 * @param {Array} conditions - The list of errorCondition objects.
 */
const ConditionsTable = ({ conditions }) => {
  if (!conditions || conditions.length === 0) {
    return null;
  }

  return (
    <ArgonBox mt={3}>
      <ArgonTypography variant="h6" fontWeight="medium" mb={1} ml={1}>
        Conditions
      </ArgonTypography>
      <TableContainer component={Paper}>
        <Table size="small" aria-label="conditions table">
          <TableHead sx={{ backgroundColor: grey[200], display: 'contents' }}>
            <TableRow>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Tested Field
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Operator
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Value
                </ArgonTypography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conditions.map((condition) => {
              // FIX: Corrected data access. 'condition' is the object, not a wrapper.
              const fields = condition.errorConditions.fields || {};
              return (
                <TableRow key={condition.sfId || condition.id}>
                  <TableCell>{fields.SBQQ__TestedField__c || 'N/A'}</TableCell>
                  <TableCell>{fields.SBQQ__Operator__c || 'N/A'}</TableCell>
                  <TableCell>{fields.SBQQ__FilterValue__c || 'N/A'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

/**
 * Renders a table for the rule's actions.
 * @param {Array} actions - The list of productAction objects.
 */
const ActionsTable = ({ actions }) => {
  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <ArgonBox mt={3}>
      <ArgonTypography variant="h6" fontWeight="medium" mb={1} ml={1}>
        Actions
      </ArgonTypography>
      <TableContainer component={Paper}>
        <Table size="small" aria-label="actions table">
          <TableHead sx={{ backgroundColor: grey[200], display: 'contents' }}>
            <TableRow>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Type
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Target
                </ArgonTypography>
              </TableCell>
              <TableCell>
                <ArgonTypography variant="caption" fontWeight="bold">
                  Value
                </ArgonTypography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.map((action) => {
              // FIX: Corrected data access. 'action' is the object, not a wrapper.
              const fields = action.productActions.fields || {};
              const target = fields.SBQQ__TargetField__c || action.productReference || 'N/A';
              const value = fields.SBQQ__Value__c || fields.SBQQ__ValueField__c || 'N/A';
              return (
                <TableRow key={action.sfId || action.id}>
                  <TableCell>{fields.SBQQ__Type__c || 'N/A'}</TableCell>
                  <TableCell>{target}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );
};

// --- Main Component ---
// MODIFICATION: Added `basicFields` prop to accept the field list from the visibility config.
function ProductRulesTab({ product, renderFieldsProp, basicFields = [] }) {
  const [expandedRule, setExpandedRule] = useState({});

  if (!product || !product.related_data?.productRules?.length) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Product Rules available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handleRuleClick = (ruleId) => {
    setExpandedRule((prevState) => ({
      ...prevState,
      [ruleId]: !prevState[ruleId],
    }));
  };

  return (
    <ArgonBox p={3}>
      {product.related_data.productRules.map((rule) => {
        const ruleId = rule.sfId || rule.id;

        // MODIFICATION: Filter the rule's fields based on the `basicFields` prop.
        const filteredFields = Object.keys(rule.fields || {})
          .filter((key) => basicFields.includes(key))
          .reduce((obj, key) => {
            obj[key] = rule.fields[key];
            return obj;
          }, {});

        return (
          <Card key={ruleId}>
            <ArgonBox mb={0.5} mt={0.5}>
              {/* --- Clickable Header for each Product Rule --- */}
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleRuleClick(ruleId)}
                style={{ cursor: 'pointer' }}
                p={1}
                borderRadius="md"
              >
                <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
                  {rule.name || rule.fields?.Name || `Rule ID: ${ruleId}`}
                </ArgonTypography>
                <IconButton
                  aria-label="expand product rule"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRuleClick(ruleId);
                  }}
                >
                  <Icon>{expandedRule[ruleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>

              {/* --- Collapsible Content for the Rule --- */}
              <Collapse in={expandedRule[ruleId]} timeout="auto" unmountOnExit>
                <ArgonBox p={2} sx={{ borderRadius: 'md' }}>
                  {/* MODIFICATION: Pass the new `filteredFields` object to the render prop. */}
                  {renderFieldsProp(filteredFields)}

                  {/* Section 2: Conditions Table */}
                  <ConditionsTable conditions={rule.errorConditions} />

                  {/* Section 3: Actions Table */}
                  <ActionsTable actions={rule.productActions} />
                </ArgonBox>
              </Collapse>
            </ArgonBox>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

export default ProductRulesTab;
